import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useAuthPopup } from '../hooks/useAuthPopup';
import TokenIcon from './TokenIcon';

// Web twin of the mobile Home feed: search + category pills + card grid.
// Cards link straight to the publisher's site (their analytics see the visit);
// authenticated sessions get per-item unlocked flags from the feed API.

// Must exactly match the strings stored in Content.categories — the backend
// filter is case-sensitive JSON_CONTAINS. These are the 7 demo-site categories.
const CATEGORIES = ['Finance', 'Sports', 'Culture', 'Tech', 'World News', 'US News', 'Entertainment'];
const SEARCH_DEBOUNCE_MS = 400;

// Fire-and-forget click-through tracking for publisher analytics. The card
// opens in a new tab, so this request completes in the still-open feed page;
// failures are deliberately swallowed — analytics must never block reading.
const recordClick = (item) => {
  if (!item.publisherId || !item.postId) return;
  api
    .post('/content/click', { publisherId: item.publisherId, postId: item.postId, source: 'web' })
    .catch(() => {});
};

const ContentCard = ({ item, isAuthenticated, onRequestLogin }) => {
  const isUnlocked = item.unlocked === true;
  const isGated = item.tokenCost > 0 && !isUnlocked;

  // Logged-out visitors browse the feed as a storefront: a card opens the login
  // popup instead of the publisher's site, and no click-through event is
  // recorded (the publisher never received the visit).
  const CardTag = isAuthenticated ? 'a' : 'button';
  const linkProps = isAuthenticated
    ? { href: item.url, target: '_blank', rel: 'noopener noreferrer', onClick: () => recordClick(item) }
    : { type: 'button', onClick: onRequestLogin };

  return (
    <CardTag
      {...linkProps}
      className="block w-full text-left bg-wp-surface rounded-xl overflow-hidden hover:opacity-90 hover:-translate-y-0.5 transition-all"
    >
      <div className="relative aspect-square bg-wp-border">
        {item.featuredImageUrl ? (
          <img src={item.featuredImageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-3xl">📰</div>
        )}
        {isGated && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center text-base">🔒</span>
          </div>
        )}
      </div>
      <div className="p-3 space-y-1">
        {isUnlocked ? (
          <p className="text-wp-success text-[11px] font-bold tracking-wide">✓ UNLOCKED</p>
        ) : isGated ? (
          <p className="flex items-center gap-1 text-wp-accent text-[11px] font-bold tracking-wide">
            <TokenIcon size={12} /> {item.tokenCost} {item.tokenCost === 1 ? 'TOKEN' : 'TOKENS'}
          </p>
        ) : null}
        <h3 className="text-white text-[15px] font-semibold leading-5 line-clamp-3">
          {item.title || 'Untitled'}
        </h3>
        {item.publisherName && (
          <p className="text-wp-muted text-[11px] tracking-widest uppercase truncate">{item.publisherName}</p>
        )}
      </div>
    </CardTag>
  );
};

const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const { openAuthPopup, isLoading: isAuthOpening, error: authError } = useAuthPopup();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [category, setCategory] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchPage = useCallback(async (cursor) => {
    const params = {};
    if (category) params.category = category;
    if (query) params.q = query;
    if (cursor) params.cursor = cursor;
    const response = await api.get('/content/feed', { params });
    return response.data;
  }, [category, query]);

  // First page (re)load on filter/search change.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError('');
    fetchPage(null)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setNextCursor(data.nextCursor);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load the feed. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [fetchPage]);

  const loadMore = async () => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const data = await fetchPage(nextCursor);
      setItems((prev) => [...prev, ...data.items]);
      setNextCursor(data.nextCursor);
    } catch {
      setError("Couldn't load more content.");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const pills = [{ label: 'For You', value: null }, ...CATEGORIES.map((c) => ({ label: c, value: c }))];

  return (
    <div>
      {!isAuthenticated && (
        <div className="bg-wp-surface rounded-xl px-4 py-3 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-wp-muted text-sm">
              <span className="text-white font-semibold">Unlock premium content across the web.</span>{' '}
              Create a free account to start reading.
            </p>
            {/* Auth0 Universal Login opens straight from here — no interstitial page. */}
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => openAuthPopup('signup')}
                disabled={isAuthOpening}
                className="px-4 py-2 rounded-xl bg-wp-accent text-wp-bg text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                Sign up
              </button>
              <button
                onClick={() => openAuthPopup('login')}
                disabled={isAuthOpening}
                className="px-4 py-2 rounded-xl bg-wp-bg text-wp-accent text-sm font-semibold hover:opacity-80 transition-opacity disabled:opacity-50"
              >
                Log in
              </button>
            </div>
          </div>
          {authError && <p className="text-wp-danger text-sm mt-2">{authError}</p>}
        </div>
      )}

      <input
        type="search"
        placeholder="Search curated content..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        className="w-full bg-wp-surface text-white placeholder-wp-muted rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-wp-accent mb-3"
      />

      <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
        {pills.map((pill) => {
          const isActive = pill.value === category;
          return (
            <button
              key={pill.label}
              onClick={() => setCategory(pill.value)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive ? 'bg-wp-accent text-wp-bg font-bold' : 'bg-wp-surface text-wp-muted hover:text-white'
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <p className="text-center text-wp-muted py-16">Loading feed…</p>
      ) : error ? (
        <p className="text-center text-wp-danger py-16">{error}</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 space-y-2">
          <p className="text-white text-lg font-semibold">Nothing here yet</p>
          <p className="text-wp-muted text-sm">
            {query || category ? 'Try a different search or category.' : 'Check back soon for new content.'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {items.map((item) => (
              <ContentCard
                key={item.id}
                item={item}
                isAuthenticated={isAuthenticated}
                onRequestLogin={() => openAuthPopup('login')}
              />
            ))}
          </div>
          {nextCursor && (
            <div className="text-center mt-6">
              <button
                onClick={loadMore}
                disabled={isLoadingMore}
                className="px-6 py-2.5 rounded-xl bg-wp-surface text-wp-accent text-sm font-semibold hover:opacity-80 transition-opacity disabled:opacity-50"
              >
                {isLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HomePage;
